import { useEffect, useId, useRef, useState } from "react";

// Date pickers that read the same everywhere: "4 Nov 2026", weeks starting on
// Sunday, whatever language the browser is set to. (The browser's own date
// box shows mm/dd/yyyy on most computers here.) Values are the same strings
// as the browser's inputs, "2026-11-04" and "2026-11-04T09:30", and a hidden
// input carries them for forms, so the server sees no difference.

const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const short = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const days = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const pad = (n: number) => String(n).padStart(2, "0");
const iso = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`;
const valid = (v: string) => /^\d{4}-\d{2}-\d{2}$/.test(v);
const todayISO = () => { const t = new Date(); return iso(t.getFullYear(), t.getMonth(), t.getDate()); };

/** showDate writes "2026-11-04" as "4 Nov 2026". */
export function showDate(v: string) {
  if (!valid(v)) return "";
  const [y, m, d] = v.split("-").map(Number);
  return `${d} ${short[m - 1]} ${y}`;
}

interface DateProps {
  name?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (v: string) => void;
  min?: string;
  max?: string;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  "aria-label"?: string;
  placeholder?: string;
}

/** useFormValue keeps a hidden form input in step: the form's reset empties
 *  an uncontrolled picker, and each change fires an "input" event so form
 *  drafts (useFormDraft) notice it like any other field. */
function useFormValue(value: string | undefined, defaultValue: string | undefined, onChange?: (v: string) => void) {
  const [own, setOwn] = useState(defaultValue ?? "");
  const v = value ?? own;
  const hidden = useRef<HTMLInputElement>(null);
  const changed = useRef(false);
  useEffect(() => {
    const form = hidden.current?.form;
    if (!form || value !== undefined) return;
    const reset = () => setOwn(defaultValue ?? "");
    form.addEventListener("reset", reset);
    return () => form.removeEventListener("reset", reset);
  }, [value, defaultValue]);
  useEffect(() => {
    if (!changed.current) return;
    changed.current = false;
    hidden.current?.dispatchEvent(new Event("input", { bubbles: true }));
  }, [v]);
  const set = (next: string) => { changed.current = true; if (value === undefined) setOwn(next); onChange?.(next); };
  return { v, set, hidden };
}

/** DateInput: a date, as "YYYY-MM-DD" (or "" when none is picked). */
export function DateInput({ name, value, defaultValue, onChange, min, max, required, disabled, id, placeholder = "Pick a date", ...rest }: DateProps) {
  const { v, set, hidden } = useFormValue(value, defaultValue, onChange);
  return (
    <>
      <Picker id={id} v={v} set={set} min={min} max={max} required={required} disabled={disabled} label={rest["aria-label"]} placeholder={placeholder} />
      <input ref={hidden} type="hidden" name={name} value={v} />
    </>
  );
}

/** DateTimeInput: a date and a time, as "YYYY-MM-DDTHH:mm" (local time). */
export function DateTimeInput({ name, value, defaultValue, onChange, min, required, disabled, id, ...rest }: DateProps) {
  const { v, set, hidden } = useFormValue(value, defaultValue, onChange);
  const [d, t] = v ? v.split("T") : ["", ""];
  return (
    <span className="datetime-input">
      <Picker id={id} v={d ?? ""} set={(nd) => set(nd ? `${nd}T${t || "09:00"}` : "")} min={min?.slice(0, 10)} required={required} disabled={disabled} label={rest["aria-label"]} placeholder="Pick a date" />
      <input type="time" step={300} aria-label="Time" value={t ?? ""} disabled={disabled || !d} onChange={(e) => d && set(`${d}T${e.target.value || "09:00"}`)} />
      <input ref={hidden} type="hidden" name={name} value={v} />
    </span>
  );
}

function Picker({ id, v, set, min, max, required, disabled, label, placeholder }: {
  id?: string; v: string; set: (v: string) => void; min?: string; max?: string; required?: boolean; disabled?: boolean; label?: string; placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const start = valid(v) ? v : valid(min ?? "") && min! > todayISO() ? min! : todayISO();
  const [view, setView] = useState(() => ({ y: Number(start.slice(0, 4)), m: Number(start.slice(5, 7)) - 1 }));
  const box = useRef<HTMLSpanElement>(null);
  const auto = useId();
  useEffect(() => {
    if (!open) return;
    const s = valid(v) ? v : start;
    setView({ y: Number(s.slice(0, 4)), m: Number(s.slice(5, 7)) - 1 });
    const away = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); setOpen(false); } };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", key, true);
    return () => { document.removeEventListener("mousedown", away); document.removeEventListener("keydown", key, true); };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const first = new Date(view.y, view.m, 1).getDay();
  const count = new Date(view.y, view.m + 1, 0).getDate();
  const cells: (number | null)[] = [...Array(first).fill(null), ...Array.from({ length: count }, (_, i) => i + 1)];
  const shift = (d: number) => setView(({ y, m }) => { const n = new Date(y, m + d, 1); return { y: n.getFullYear(), m: n.getMonth() }; });
  const today = todayISO();
  const off = (day: string) => (!!min && day < min) || (!!max && day > max);
  return (
    <span className="date-input" ref={box}>
      <button type="button" id={id ?? auto} className={`date-button${v ? "" : " empty"}`} disabled={disabled} aria-haspopup="dialog" aria-expanded={open} aria-label={label ? `${label}: ${v ? showDate(v) : "none"}` : undefined} onClick={() => setOpen(!open)}>
        <svg viewBox="0 0 24 24" aria-hidden><path d="M5 6.5h14a1 1 0 0 1 1 1V19a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7.5a1 1 0 0 1 1-1zM4 10.5h16M8.5 4v4M15.5 4v4" /></svg>
        <span>{v ? showDate(v) : placeholder}</span>
      </button>
      {/* Lets the browser stop a form when a required date is missing. */}
      {required && <input className="date-required" tabIndex={-1} aria-hidden required value={v} onChange={() => undefined} />}
      {open && (
        <div className="date-pop" role="dialog" aria-label="Choose a date">
          <div className="date-head">
            <button type="button" className="icon-btn" aria-label="Previous month" onClick={() => shift(-1)}>‹</button>
            <b>{months[view.m]} {view.y}</b>
            <button type="button" className="icon-btn" aria-label="Next month" onClick={() => shift(1)}>›</button>
          </div>
          <div className="date-grid">
            {days.map((d) => <span key={d} className="date-dow">{d}</span>)}
            {cells.map((d, i) => {
              if (d === null) return <span key={`x${i}`} />;
              const day = iso(view.y, view.m, d);
              return (
                <button key={day} type="button" disabled={off(day)} aria-pressed={day === v} aria-label={showDate(day)}
                  className={[day === v && "on", day === today && "today"].filter(Boolean).join(" ") || undefined}
                  onClick={() => { set(day); setOpen(false); }}>{d}</button>
              );
            })}
          </div>
          <div className="date-foot">
            <button type="button" className="link small" disabled={off(today)} onClick={() => { set(today); setOpen(false); }}>Today</button>
            {!required && v && <button type="button" className="link small" onClick={() => { set(""); setOpen(false); }}>Clear</button>}
          </div>
        </div>
      )}
    </span>
  );
}
